import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { apiGet } from '../../services/api/client';
import { useProjectRoles } from '../../hooks/useLeads';
import { useProject } from '../../hooks/useProjects';
import { TIME_AND_MATERIAL_PAYMENT_TYPE_ID } from '../../constants/typeIds';
import { COMMON_CURRENCIES } from '../../services/exchangeRate';
import type {
  ProjectAssignment,
  CreateAssignmentInput,
  UpdateAssignmentInput,
  AssignmentAllocationInput,
  AssignmentStatus,
} from '../../services/api/projects';
import type { HolidaysResponse } from '../../services/api/timeEntry';
import { parseDecimalInput } from '../../utils/parseDecimalInput';
import { useFlag } from '../../services/flagsContext';
import { formatMonthYear } from '../../utils/dateFormat';
import { DAILY_TARGET_HOURS, getMonthlyTargetHours } from '../../utils/workingHours';
import { DateField } from '../ui/DateField';

// Phase 70 (currency-aware billing rates) — the backend only accepts
// CZK/EUR/USD for billingRateCurrency (400 on anything else). The wider
// COMMON_CURRENCIES list is used elsewhere for local-only display/convert
// helpers; here the selector must match the persisted enum exactly.
const BILLING_RATE_CURRENCIES = COMMON_CURRENCIES.filter((c) => c.code === 'CZK' || c.code === 'EUR' || c.code === 'USD');
type BillingRateCurrency = 'CZK' | 'EUR' | 'USD';

/**
 * MKS-358 — the allocation is ONE figure the planner may type either way
 * round: as a share of the person's FTE, or as hours a month. The backend
 * stores whichever was typed and converts the other side against the person's
 * real FTE, their real working days and the assignment's dates.
 *
 * The form cannot do that conversion exactly — it has neither the person's FTE
 * nor their time off — so the live figure below is the NOMINAL one: a full 1.0
 * FTE month of holiday-aware working days, from the same `workingHours` util
 * the timesheet targets use. It is labelled as such rather than presented as
 * the stored number, and it is only a preview: whichever side the user typed
 * is what gets sent.
 */
type AllocationUnit = 'percent' | 'hours';

/** Round to `places` decimals without the float dust `toFixed` leaves behind. */
function round(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

/** Drop a trailing `.0` so a whole number reads as one. */
function formatDerived(value: number): string {
  return String(round(value, 1));
}

interface User {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  currentHourlyCosts: number | null;
}

interface AssignmentFormProps {
  projectId: number;
  assignment?: ProjectAssignment | null;
  onSubmit: (input: CreateAssignmentInput | UpdateAssignmentInput) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export default function AssignmentForm({ projectId, assignment, onSubmit, onCancel, isLoading }: AssignmentFormProps) {
  const isEditing = !!assignment;

  // MKS-277 — on a Time & Material project there is ONE billing rate per role:
  // the project's per-role rate, which is what the invoice bills from. The
  // field below edits that rate rather than a second number private to this
  // person, so it is labelled and warned about accordingly, and it needs a
  // role to belong to.
  const { data: project } = useProject(projectId);
  const isRoleRate = project?.paymentTypeId === TIME_AND_MATERIAL_PAYMENT_TYPE_ID;

  // Fetch users for the dropdown
  const { data: users } = useQuery<User[]>({
    queryKey: ['users-list'],
    queryFn: () => apiGet<{ data: User[] }>('/admin/users').then((res) => res.data),
    enabled: !isEditing, // Only fetch when creating new assignment
  });

  const { data: projectRolesData } = useProjectRoles();
  const projectRoles = projectRolesData?.data ?? [];

  // Role name combobox state
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const roleInputRef = useRef<HTMLInputElement>(null);
  const roleDropdownRef = useRef<HTMLDivElement>(null);

  // Close role dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
        setIsRoleDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [form, setForm] = useState({
    userId: '',
    startDate: '',
    endDate: '',
    billingRate: '',
    hourlyCosts: '',
    userInvestment: '',
    roleName: '',
  });
  const [error, setError] = useState<string | null>(null);

  // MKS-358 — the staffing half of the form, behind its own flag. With the flag
  // off nothing below renders and no staffing field is sent, so the form is the
  // one it was.
  const allocationUi = useFlag('mks_358_assignment_allocation_ui');
  const [allocationUnit, setAllocationUnit] = useState<AllocationUnit>('percent');
  const [allocation, setAllocation] = useState('');
  const [status, setStatus] = useState<AssignmentStatus>('confirmed');
  /**
   * Has the user touched either staffing control? An EDIT that did not sends
   * neither key, so the row's allocation and status are left alone rather than
   * overwritten with what this form happened to be seeded with. That matters
   * wherever the form is handed an assignment without its staffing fields —
   * `UserDetailPage` builds one from the user's own assignment list — where
   * saving a rate change would otherwise clear the allocation and confirm a
   * tentative row.
   */
  const [staffingTouched, setStaffingTouched] = useState(false);

  // Phase 70 (currency-aware billing rates) — the currency selector now
  // PERSISTS billingRateCurrency with the assignment instead of converting
  // the entered amount to CZK and discarding the currency (the old
  // one-shot "Convert to CZK" behavior). The backend resolves foreign
  // amounts to CZK at read/lock time (see assignmentRateResolverService).
  const [currency, setCurrency] = useState<BillingRateCurrency>('CZK');

  const currencyInfo = BILLING_RATE_CURRENCIES.find((c) => c.code === currency);
  const billingRateLabel = currency === 'CZK' ? 'CZK/hr' : `${currencyInfo?.symbol ?? currency}/hr`;

  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency as BillingRateCurrency);
  };

  useEffect(() => {
    if (assignment) {
      // When editing, fall back to user's default hourly costs if assignment doesn't have one
      const hourlyCosts = assignment.hourlyCosts ?? assignment.user?.defaultHourlyCosts ?? null;
      setForm({
        userId: assignment.userId?.toString() || '',
        startDate: assignment.startDate || '',
        endDate: assignment.endDate || '',
        billingRate: assignment.billingRate?.toString() || '',
        hourlyCosts: hourlyCosts?.toString() || '',
        userInvestment: assignment.userInvestment?.toString() || '',
        roleName: assignment.roleName || '',
      });
      setCurrency(assignment.billingRateCurrency ?? 'CZK');
      // MKS-358 — seed the side the row actually stores, so re-saving an
      // untouched form sends back what it read instead of a converted figure.
      if (assignment.allocationHours != null) {
        setAllocationUnit('hours');
        setAllocation(String(assignment.allocationHours));
      } else if (assignment.allocationShare != null) {
        setAllocationUnit('percent');
        setAllocation(String(round(assignment.allocationShare * 100, 2)));
      } else {
        setAllocation('');
      }
      setStatus(assignment.status ?? 'confirmed');
      setStaffingTouched(false);
    }
  }, [assignment]);

  // MKS-358 — the month the nominal conversion is quoted in: the one the
  // assignment starts in, else this one. Months differ by up to two working
  // days, so naming it is the difference between a figure and a guess.
  const basisDate = useMemo(() => {
    const parsed = form.startDate ? new Date(`${form.startDate}T00:00:00`) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
  }, [form.startDate]);

  // The same `['holidays', year]` query every other working-hours surface uses
  // (TimeEntryPage, CurrentMonthTimesheetCard), called through `apiGet`
  // directly rather than `timeEntry.getHolidays` so this form keeps its one
  // API-module dependency.
  const { data: holidaysData } = useQuery({
    queryKey: ['holidays', basisDate.getFullYear()],
    queryFn: () => apiGet<HolidaysResponse>(`/holidays?year=${basisDate.getFullYear()}`),
    staleTime: 24 * 60 * 60 * 1000,
    enabled: allocationUi,
  });

  /** Hours a full 1.0 FTE works in the basis month. */
  const basisMonthHours = useMemo(() => {
    const holidayDates = new Set<string>();
    for (const holiday of holidaysData?.data ?? []) holidayDates.add(holiday.date);
    return getMonthlyTargetHours(basisDate.getFullYear(), basisDate.getMonth(), holidayDates);
  }, [holidaysData, basisDate]);

  // MKS-351 — month labels go through `utils/dateFormat`, never a literal locale.
  const basisMonthLabel = formatMonthYear(basisDate);

  /** The typed figure in the other unit, or null when there is nothing to derive from. */
  const derivedAllocation = useMemo(() => {
    const value = parseDecimalInput(allocation);
    if (value === null || value <= 0 || basisMonthHours <= 0) return null;
    return allocationUnit === 'percent'
      ? (value / 100) * basisMonthHours
      : (value / basisMonthHours) * 100;
  }, [allocation, allocationUnit, basisMonthHours]);

  /** Switching the unit converts the figure, so the user keeps the allocation they saw. */
  const handleAllocationUnitChange = (nextUnit: AllocationUnit) => {
    if (nextUnit === allocationUnit) return;
    setStaffingTouched(true);
    setAllocationUnit(nextUnit);
    if (derivedAllocation !== null) setAllocation(String(round(derivedAllocation, 2)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      // F-1907: reject inverted date ranges early (endDate null is always ok).
      if (form.startDate && form.endDate && new Date(form.startDate) > new Date(form.endDate)) {
        setError('Start date must be on or before the end date.');
        return;
      }

      // MKS-358 — exactly one allocation figure, in the unit the user typed.
      // An empty field means "no allocation": `null` clears it, which is a
      // different request from omitting the key (leave it alone) and from an
      // empty object (a 400).
      let staffing: { status: AssignmentStatus; allocation: AssignmentAllocationInput | null } | undefined;
      if (allocationUi && (staffingTouched || !isEditing)) {
        const value = parseDecimalInput(allocation);
        if (value !== null && value <= 0) {
          setError('An allocation must be greater than zero.');
          return;
        }
        if (allocationUnit === 'percent' && value !== null && value > 100) {
          setError("An allocation cannot be more than 100 % of the person's FTE.");
          return;
        }
        staffing = {
          status,
          allocation:
            value === null
              ? null
              : allocationUnit === 'percent'
                ? { share: round(value / 100, 4) }
                : { hours: round(value, 2) },
        };
      }

      if (isEditing) {
        const input: UpdateAssignmentInput = {
          startDate: form.startDate || null,
          endDate: form.endDate || null,
          billingRate: form.billingRate ? parseFloat(form.billingRate) : null,
          billingRateCurrency: currency,
          hourlyCosts: form.hourlyCosts ? parseFloat(form.hourlyCosts) : null,
          userInvestment: form.userInvestment ? parseFloat(form.userInvestment) : null,
          roleName: form.roleName || null,
          ...staffing,
        };
        await onSubmit(input);
      } else {
        if (!form.userId) {
          setError('Please select a user.');
          return;
        }
        const input: CreateAssignmentInput = {
          userId: parseInt(form.userId, 10),
          startDate: form.startDate || undefined,
          endDate: form.endDate || undefined,
          billingRate: form.billingRate ? parseFloat(form.billingRate) : undefined,
          billingRateCurrency: currency,
          hourlyCosts: form.hourlyCosts ? parseFloat(form.hourlyCosts) : undefined,
          userInvestment: form.userInvestment ? parseFloat(form.userInvestment) : undefined,
          roleName: form.roleName || undefined,
          ...staffing,
        };
        await onSubmit(input);
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to save assignment.');
    }
  };

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUserChange = (userId: string) => {
    const selectedUser = users?.find((u) => u.id === parseInt(userId, 10));
    setForm((prev) => ({
      ...prev,
      userId,
      // Prefill hourly costs from user's current rate, reset investment
      hourlyCosts: selectedUser?.currentHourlyCosts?.toString() || '',
      userInvestment: '',
    }));
  };

  const handleInvestmentChange = (value: string) => {
    const newInvestment = parseDecimalInput(value) ?? 0;
    const currentInvestment = parseDecimalInput(form.userInvestment) ?? 0;
    const currentHourlyCosts = parseDecimalInput(form.hourlyCosts) ?? 0;

    // Calculate the change in investment and subtract from hourly costs
    const investmentDelta = newInvestment - currentInvestment;
    const newHourlyCosts = Math.max(0, currentHourlyCosts - investmentDelta);

    setForm((prev) => ({
      ...prev,
      userInvestment: value,
      hourlyCosts: newHourlyCosts.toString(),
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
      {isEditing && assignment?.user && (
        <div className="text-sm font-medium text-gray-700">
          Editing: <span className="text-gray-900">{assignment.user.name}</span>
          <span className="text-gray-500 ml-1">({assignment.user.email})</span>
        </div>
      )}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {!isEditing && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">User</label>
            <select
              value={form.userId}
              onChange={(e) => handleUserChange(e.target.value)}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              required
            >
              <option value="">Select a user...</option>
              {users?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.lastName || user.firstName
                    ? `${user.lastName ?? ''} ${user.firstName ?? ''}`.trim()
                    : user.email}
                  {' '}({user.email})
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
          <DateField
            value={form.startDate}
            onChange={(nextDate) => updateField('startDate', nextDate)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
          <DateField
            value={form.endDate}
            onChange={(nextDate) => updateField('endDate', nextDate)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
          <select
            value={currency}
            onChange={(e) => handleCurrencyChange(e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {BILLING_RATE_CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} ({c.symbol})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="assignment-billing-rate" className="block text-xs font-medium text-gray-600 mb-1">
            {isRoleRate
              ? `Billing Rate for ${form.roleName.trim() || 'this role'} (${billingRateLabel})`
              : `Billing Rate (${billingRateLabel})`}
          </label>
          <input
            id="assignment-billing-rate"
            type="number"
            step="0.01"
            min="0"
            value={form.billingRate}
            onChange={(e) => updateField('billingRate', e.target.value)}
            disabled={isRoleRate && !form.roleName.trim()}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-400"
            placeholder="0.00"
          />
          {/* MKS-277 — on a Time & Material project this is the project's rate
              for the role, not a number private to this person. Say so, so
              nobody edits it expecting to move one person's rate. */}
          {isRoleRate && (
            <p className="mt-1 text-xs text-amber-700">
              {form.roleName.trim()
                ? `This is the rate for ${form.roleName.trim()} on this project. Changing it changes it for everyone in that role.`
                : 'Pick a role first — on a Time & Material project the billing rate belongs to the role.'}
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Hourly Costs (Kč/hr)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.hourlyCosts}
            onChange={(e) => updateField('hourlyCosts', e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder="0.00"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Investment Rate (Kč/hr)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.userInvestment}
            onChange={(e) => handleInvestmentChange(e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder="0.00"
          />
        </div>
      </div>

      {/* Foreign-currency hint — the amount above is stored + billed in the
          selected currency; conversion to CZK happens read-time/lock-time
          on the backend (provisional while the period is open, locked
          after period close). */}
      {currency !== 'CZK' && (
        <p className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          This rate is stored in {currency}. The CZK equivalent shown elsewhere (to-be-invoiced,
          reports) is converted automatically using the latest exchange rate while the period is
          open, and locked to a fixed CZK value once the period closes.
        </p>
      )}

      {/* MKS-358 — the staffing half: how much of the person this assignment
          books, and whether it is a plan or a commitment. */}
      {allocationUi && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-gray-200 pt-3">
          <div>
            <label htmlFor="assignment-allocation" className="block text-xs font-medium text-gray-600 mb-1">
              Allocation
            </label>
            <div className="flex gap-2">
              <input
                id="assignment-allocation"
                type="number"
                step="0.01"
                min="0"
                value={allocation}
                onChange={(e) => {
                  setStaffingTouched(true);
                  setAllocation(e.target.value);
                }}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="0"
              />
              <select
                aria-label="Allocation unit"
                value={allocationUnit}
                onChange={(e) => handleAllocationUnitChange(e.target.value as AllocationUnit)}
                className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="percent">% of FTE</option>
                <option value="hours">h / month</option>
              </select>
            </div>
            <p className="mt-1 text-xs text-gray-700" data-testid="allocation-derived">
              {derivedAllocation === null
                ? `A full 1.0 FTE works ${formatDerived(basisMonthHours)} h in ${basisMonthLabel}.`
                : allocationUnit === 'percent'
                  ? `≈ ${formatDerived(derivedAllocation)} h in ${basisMonthLabel}`
                  : `≈ ${formatDerived(derivedAllocation)} % of FTE in ${basisMonthLabel}`}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Nominal, at a full 1.0 FTE ({formatDerived(basisMonthHours / DAILY_TARGET_HOURS)} working
              days &times; {DAILY_TARGET_HOURS} h). The person&rsquo;s own FTE and the assignment&rsquo;s
              dates set the figure the planner shows.
            </p>
          </div>
          <div>
            <label htmlFor="assignment-status" className="block text-xs font-medium text-gray-600 mb-1">
              Status
            </label>
            <select
              id="assignment-status"
              value={status}
              onChange={(e) => {
                setStaffingTouched(true);
                setStatus(e.target.value as AssignmentStatus);
              }}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="confirmed">Confirmed</option>
              <option value="tentative">Tentative</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              A tentative assignment is a plan: it books capacity and grants no access to the project
              until it is confirmed.
            </p>
          </div>
        </div>
      )}

      <div ref={roleDropdownRef} className="max-w-xs">
        <label className="block text-xs font-medium text-gray-600 mb-1">Role Name</label>
        <div className="relative">
          <input
            ref={roleInputRef}
            type="text"
            value={form.roleName}
            onChange={(e) => {
              updateField('roleName', e.target.value);
              setRoleFilter(e.target.value);
              if (!isRoleDropdownOpen) setIsRoleDropdownOpen(true);
            }}
            onFocus={() => setIsRoleDropdownOpen(true)}
            placeholder="Select or type a role..."
            className="w-full rounded border border-gray-300 px-2 py-1.5 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <button
            type="button"
            onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
          >
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${isRoleDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {isRoleDropdownOpen && (() => {
            const filtered = projectRoles.filter((r) => {
              const label = r.code ? `${r.code} — ${r.name}` : r.name;
              return label.toLowerCase().includes(roleFilter.toLowerCase());
            });
            const isNew = form.roleName.trim() !== '' &&
              !projectRoles.some((r) => r.name.toLowerCase() === form.roleName.trim().toLowerCase());

            return (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-auto">
                {filtered.length === 0 && !form.roleName.trim() && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No existing roles. Type to add a new one.
                  </div>
                )}
                {filtered.map((r) => {
                  const label = r.code ? `${r.code} — ${r.name}` : r.name;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => {
                        updateField('roleName', r.name);
                        setRoleFilter('');
                        setIsRoleDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                        r.name.toLowerCase() === form.roleName.toLowerCase() ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
                {isNew && (
                  <button
                    type="button"
                    onClick={() => {
                      updateField('roleName', form.roleName.trim());
                      setRoleFilter('');
                      setIsRoleDropdownOpen(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 text-green-700 border-t border-gray-100 flex items-center gap-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add &ldquo;{form.roleName.trim()}&rdquo;
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" size="sm" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" type="submit" isLoading={isLoading}>
          {isEditing ? 'Update' : 'Add'}
        </Button>
      </div>
    </form>
  );
}
